(() => {
  const init = () => {
    const updateScrollState = () => {
      document.body.classList.toggle('tdb-has-scrolled', window.scrollY > 96);
    };

    const overlay = document.querySelector('[data-tdb-menu-overlay]');
    const openButtons = document.querySelectorAll('[data-tdb-menu-open]');
    const closeButtons = document.querySelectorAll('[data-tdb-menu-close]');
    let closeAllDrawers = () => {};

    const openMenu = () => {
      if (!overlay) return;
      closeAllDrawers(true);
      overlay.hidden = false;
      document.body.classList.add('tdb-menu-is-open');
    };

    const closeMenu = () => {
      if (!overlay) return;
      overlay.hidden = true;
      document.body.classList.remove('tdb-menu-is-open');
    };

    openButtons.forEach((button) => button.addEventListener('click', openMenu));
    closeButtons.forEach((button) => button.addEventListener('click', closeMenu));
    overlay?.addEventListener('click', (event) => {
      if (event.target === overlay || event.target.classList.contains('tdb-menu-backdrop')) closeMenu();
    });

    const drawers = Array.from(document.querySelectorAll('[data-tdb-drawer]'));
    const drawerTriggers = new WeakMap();
    const drawerTimers = new WeakMap();

    const finishDrawerClose = (drawer, restoreFocus) => {
      drawer.hidden = true;
      drawer.setAttribute('aria-hidden', 'true');
      const trigger = drawerTriggers.get(drawer);
      trigger?.setAttribute('aria-expanded', 'false');
      if (restoreFocus) trigger?.focus();
      if (!drawers.some((item) => item.classList.contains('is-open'))) {
        document.body.classList.remove('tdb-drawer-is-open');
      }
    };

    const closeDrawer = (drawer, immediate = false, restoreFocus = true) => {
      if (!drawer || drawer.hidden) return;
      const existingTimer = drawerTimers.get(drawer);
      if (existingTimer) window.clearTimeout(existingTimer);
      drawer.classList.remove('is-open');
      if (immediate) {
        finishDrawerClose(drawer, restoreFocus);
        return;
      }
      const timer = window.setTimeout(() => finishDrawerClose(drawer, restoreFocus), 240);
      drawerTimers.set(drawer, timer);
    };

    closeAllDrawers = (immediate = false) => {
      drawers.forEach((drawer) => closeDrawer(drawer, immediate, false));
    };

    const openDrawer = (drawer, trigger) => {
      if (!drawer) return;
      drawers.forEach((otherDrawer) => {
        if (otherDrawer !== drawer) closeDrawer(otherDrawer, true, false);
      });
      const existingTimer = drawerTimers.get(drawer);
      if (existingTimer) window.clearTimeout(existingTimer);
      drawerTriggers.set(drawer, trigger);
      trigger?.setAttribute('aria-expanded', 'true');
      drawer.hidden = false;
      drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('tdb-drawer-is-open');
      window.requestAnimationFrame(() => drawer.classList.add('is-open'));
      window.setTimeout(() => drawer.querySelector('.tdb-side-drawer__close')?.focus(), 0);
    };

    drawers.forEach((drawer) => {
      drawer.querySelectorAll('[data-tdb-drawer-close]').forEach((button) => {
        button.addEventListener('click', () => closeDrawer(drawer));
      });
    });

    const cartDrawer = document.querySelector('[data-tdb-cart-drawer]');
    const accountDrawer = document.querySelector('[data-tdb-account-drawer]');
    const cartItems = cartDrawer?.querySelector('[data-tdb-cart-items]');
    const cartEmpty = cartDrawer?.querySelector('[data-tdb-cart-empty]');
    const cartStatus = cartDrawer?.querySelector('[data-tdb-cart-status]');
    const cartSubtotal = cartDrawer?.querySelector('[data-tdb-cart-subtotal]');
    const cartCheckout = cartDrawer?.querySelector('[data-tdb-cart-checkout]');
    const cartRecommendations = cartDrawer?.querySelector('[data-tdb-cart-recommendations]');
    const shopRoot = window.Shopify?.routes?.root || '/';
    const cartEndpoint = `${shopRoot}cart.js`;
    const cartAddEndpoint = `${shopRoot}cart/add.js`;
    const cartChangeEndpoint = `${shopRoot}cart/change.js`;

    const escapeHTML = (value) => String(value ?? '').replace(/[&<>"']/g, (character) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    })[character]);

    let moneyFormatter;
    try {
      moneyFormatter = new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {
        style: 'currency',
        currency: cartDrawer?.dataset.currency || 'AUD'
      });
    } catch (error) {
      moneyFormatter = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' });
    }

    const formatMoney = (cents) => moneyFormatter.format(Number(cents || 0) / 100);

    const showCartStatus = (message, isError = false) => {
      if (!cartStatus) return;
      cartStatus.textContent = message;
      cartStatus.classList.toggle('is-error', isError);
      cartStatus.hidden = !message;
    };

    const requestJSON = async (url, options = {}) => {
      const response = await fetch(url, { credentials: 'same-origin', ...options });
      let data = {};
      try {
        data = await response.json();
      } catch (error) {
        data = {};
      }
      if (!response.ok) {
        throw new Error(data.description || data.message || 'Please try again.');
      }
      return data;
    };

    const updateCartRecommendations = (cart) => {
      const productIds = new Set(cart.items.map((item) => String(item.product_id)));
      const recommendations = Array.from(cartDrawer?.querySelectorAll('[data-tdb-cart-recommendation]') || []);

      recommendations.forEach((recommendation) => {
        recommendation.hidden = productIds.has(recommendation.dataset.tdbCartRecommendation);
      });

      if (cartRecommendations) {
        cartRecommendations.hidden = recommendations.length === 0 || recommendations.every((recommendation) => recommendation.hidden);
      }
    };

    const renderCart = (cart) => {
      if (!cartItems || !cartEmpty || !cartSubtotal || !cartCheckout) return;

      cartItems.innerHTML = cart.items.map((item) => {
        const visibleProperties = Object.entries(item.properties || {})
          .filter(([key, value]) => key.charAt(0) !== '_' && value)
          .map(([key, value]) => `${key}: ${value}`);
        const variantTitle = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
        const meta = [variantTitle, ...visibleProperties].filter(Boolean).join(' / ');
        const image = item.image
          ? `<img src="${escapeHTML(item.image)}" alt="${escapeHTML(item.product_title)}" loading="lazy">`
          : '';

        return `
          <article class="tdb-cart-drawer__item">
            <a class="tdb-cart-drawer__item-media" href="${escapeHTML(item.url)}">${image}</a>
            <div class="tdb-cart-drawer__item-copy">
              <a class="tdb-cart-drawer__item-title" href="${escapeHTML(item.url)}">${escapeHTML(item.product_title)}</a>
              ${meta ? `<p>${escapeHTML(meta)}</p>` : ''}
              <span>Quantity: ${item.quantity}</span>
              <button type="button" data-tdb-cart-remove="${escapeHTML(item.key)}">Remove</button>
            </div>
            <strong>${formatMoney(item.final_line_price)}</strong>
          </article>`;
      }).join('');

      const isEmpty = cart.item_count === 0;
      cartEmpty.hidden = !isEmpty;
      cartSubtotal.textContent = formatMoney(cart.total_price);
      cartCheckout.classList.toggle('is-disabled', isEmpty);
      cartCheckout.setAttribute('aria-disabled', String(isEmpty));
      document.querySelectorAll('[data-tdb-cart-count]').forEach((count) => {
        count.textContent = String(cart.item_count);
      });
      updateCartRecommendations(cart);
      showCartStatus('');
    };

    const refreshCart = async () => {
      const cart = await requestJSON(cartEndpoint, { headers: { Accept: 'application/json' } });
      renderCart(cart);
      return cart;
    };

    document.querySelectorAll('[data-tdb-cart-open]').forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        if (!cartDrawer) return;
        event.preventDefault();
        closeMenu();
        openDrawer(cartDrawer, trigger);
        refreshCart().catch((error) => showCartStatus(error.message, true));
      });
    });

    document.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      const trigger = event.target.closest('[data-tdb-account-open]');
      if (!trigger || !accountDrawer) return;
      event.preventDefault();
      closeMenu();
      openDrawer(accountDrawer, trigger);
    });

    document.querySelectorAll('.tdb-product-form').forEach((form) => {
      form.addEventListener('submit', async (event) => {
        if (!cartDrawer) return;
        event.preventDefault();
        const submitButton = form.querySelector('button[type="submit"]');
        const originalLabel = submitButton?.textContent || 'Add To Cart';
        const wasDisabled = Boolean(submitButton?.disabled);
        if (submitButton) {
          submitButton.disabled = true;
          submitButton.textContent = 'Adding...';
        }

        try {
          const productFormData = new FormData(form);
          const variantId = Number(productFormData.get('id'));
          const properties = {};
          productFormData.forEach((value, key) => {
            if (key.startsWith('properties[') && key.endsWith(']') && value) {
              properties[key.slice(11, -1)] = value;
            }
          });

          if (!variantId) throw new Error('Please select an available option.');

          await requestJSON(cartAddEndpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: variantId, quantity: 1, properties }] })
          });
          await refreshCart();
          openDrawer(cartDrawer, submitButton);
        } catch (error) {
          openDrawer(cartDrawer, submitButton);
          showCartStatus(error.message, true);
        } finally {
          if (submitButton) {
            submitButton.disabled = wasDisabled;
            submitButton.textContent = originalLabel;
          }
        }
      });
    });

    cartDrawer?.addEventListener('click', async (event) => {
      if (!(event.target instanceof Element)) return;
      const removeButton = event.target.closest('[data-tdb-cart-remove]');
      const quickAddButton = event.target.closest('[data-tdb-quick-add]');

      if (removeButton) {
        removeButton.disabled = true;
        showCartStatus('Updating your bag...');
        try {
          await requestJSON(cartChangeEndpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: removeButton.dataset.tdbCartRemove, quantity: 0 })
          });
          await refreshCart();
        } catch (error) {
          removeButton.disabled = false;
          showCartStatus(error.message, true);
        }
        return;
      }

      if (quickAddButton) {
        const originalLabel = quickAddButton.textContent;
        quickAddButton.disabled = true;
        quickAddButton.textContent = 'Adding...';
        try {
          await requestJSON(cartAddEndpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: Number(quickAddButton.dataset.tdbQuickAdd), quantity: 1 }] })
          });
          await refreshCart();
          quickAddButton.textContent = 'Added';
          window.setTimeout(() => {
            quickAddButton.disabled = false;
            quickAddButton.textContent = originalLabel;
          }, 900);
        } catch (error) {
          quickAddButton.disabled = false;
          quickAddButton.textContent = originalLabel;
          showCartStatus(error.message, true);
        }
      }
    });

    window.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      closeMenu();
      drawers.forEach((drawer) => closeDrawer(drawer));
    });

    document.querySelectorAll('.tdb-product-form').forEach((form) => {
      const picker = form.querySelector('[data-tdb-material-picker]');
      const variantSelect = form.querySelector('select[name="id"]');
      const propertyInput = form.querySelector('[data-tdb-material-property]');
      const variantData = form.querySelector('[data-tdb-variant-json]');
      const optionSelects = Array.from(form.querySelectorAll('[data-tdb-product-option]'));
      const materialPosition = Number(picker?.dataset.optionPosition || 0);
      const productRoot = form.closest('.tdb-product') || document;
      const submitButton = form.querySelector('button[type="submit"]');
      const zoomAddButton = productRoot.querySelector('[data-tdb-product-zoom-add]');
      const priceElements = Array.from(productRoot.querySelectorAll('[data-tdb-product-price]'));
      const priceNotes = Array.from(productRoot.querySelectorAll('[data-tdb-product-price-note]'));
      const currency = form.dataset.currency || 'AUD';
      let variants = [];

      try {
        variants = JSON.parse(variantData?.textContent || '[]');
      } catch (error) {
        variants = [];
      }

      if (!variantSelect || variants.length === 0) return;

      const selectedVariant = variants.find((variant) => String(variant.id) === variantSelect.value) || variants[0];
      const selectedOptions = [selectedVariant.option1, selectedVariant.option2, selectedVariant.option3];
      const formatPrice = (price) => new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol'
      }).format(Number(price || 0) / 100);

      const renderVariant = (variant) => {
        if (!variant) return;

        variantSelect.value = String(variant.id);
        const isPriced = Number(variant.price) > 0;
        const canPurchase = Boolean(variant.available && isPriced);
        const actionLabel = !isPriced ? 'Coming Soon' : (variant.available ? 'Add To Cart' : 'Sold Out');
        const priceLabel = isPriced ? formatPrice(variant.price) : 'Coming soon';

        priceElements.forEach((element) => {
          element.textContent = priceLabel;
        });
        priceNotes.forEach((element) => {
          element.hidden = !isPriced;
        });

        if (submitButton) {
          submitButton.disabled = !canPurchase;
          submitButton.textContent = actionLabel;
        }
        if (zoomAddButton) {
          zoomAddButton.disabled = !canPurchase;
          zoomAddButton.textContent = actionLabel;
        }
      };

      const updateVariant = () => {
        const variant = variants.find((candidate) => selectedOptions.every((value, index) => {
          return !value || candidate[`option${index + 1}`] === value;
        }));
        renderVariant(variant);
      };

      picker?.querySelectorAll('input[type="radio"]').forEach((input) => {
        const updateMaterial = () => {
          picker.querySelectorAll('.tdb-material-option').forEach((label) => {
            label.classList.toggle('is-selected', label.contains(input));
          });
          if (propertyInput) propertyInput.value = input.value;
          if (materialPosition) selectedOptions[materialPosition - 1] = input.value;
          updateVariant();
        };

        if (input.checked) updateMaterial();
        input.addEventListener('change', updateMaterial);
      });

      optionSelects.forEach((optionSelect) => {
        const optionPosition = Number(optionSelect.dataset.optionPosition || 0);
        if (!optionPosition) return;
        selectedOptions[optionPosition - 1] = optionSelect.value;
        optionSelect.addEventListener('change', () => {
          selectedOptions[optionPosition - 1] = optionSelect.value;
          updateVariant();
        });
      });

      updateVariant();
    });

    document.querySelectorAll('[data-tdb-product-gallery]').forEach((gallery) => {
      const media = gallery.closest('.tdb-product-hero__media');
      const slides = Array.from(gallery.querySelectorAll('[data-tdb-product-slide]'));
      const previous = media?.querySelector('[data-tdb-gallery-previous]');
      const next = media?.querySelector('[data-tdb-gallery-next]');
      const dots = Array.from(media?.querySelectorAll('[data-tdb-gallery-dots] span') || []);
      let activeIndex = 0;

      const showSlide = (index) => {
        if (slides.length < 2) return;
        activeIndex = (index + slides.length) % slides.length;
        slides.forEach((slide, slideIndex) => slide.classList.toggle('is-active', slideIndex === activeIndex));
        dots.forEach((dot, dotIndex) => dot.classList.toggle('is-active', dotIndex === activeIndex));
      };

      previous?.addEventListener('click', () => showSlide(activeIndex - 1));
      next?.addEventListener('click', () => showSlide(activeIndex + 1));
    });

    updateScrollState();
    window.addEventListener('scroll', updateScrollState, { passive: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
